import * as THREE from 'three';
import type { Random } from '../../core/Random';
import { box, cyl, M, mat, mesh, registerNightGlow, sphere, texMat, worldBox } from '../materials';
import { chainLinkTexture, plaidTexture, signTexture, woodTexture, type SignSpec } from '../textures';

/** All builders: origin on the ground, "front" faces +z unless noted. */

export function couch(fabric: string): THREE.Group {
  const g = new THREE.Group();
  const f = texMat(plaidTexture(fabric));
  g.add(box(2.0, 0.42, 0.9, f, 0, 0.21 + 0.06, 0));
  g.add(box(2.0, 0.55, 0.22, f, 0, 0.62, -0.34));
  g.add(box(0.22, 0.3, 0.9, f, -0.95, 0.55, 0));
  g.add(box(0.22, 0.3, 0.9, f, 0.95, 0.55, 0));
  for (const x of [-0.45, 0.45]) g.add(box(0.86, 0.12, 0.66, f, x, 0.53, 0.08));
  for (const x of [-0.9, 0.9])
    for (const z of [-0.35, 0.35]) g.add(box(0.08, 0.06, 0.08, M.black, x, 0.03, z));
  return g;
}

export function recliner(color: string): THREE.Group {
  const g = new THREE.Group();
  const m = mat(color, { rough: 0.95 });
  g.add(box(0.95, 0.45, 0.9, m, 0, 0.25, 0));
  const back = box(0.95, 0.75, 0.25, m, 0, 0.75, -0.42);
  back.rotation.x = -0.25;
  g.add(back);
  g.add(box(0.18, 0.28, 0.85, m, -0.45, 0.6, 0));
  g.add(box(0.18, 0.28, 0.85, m, 0.45, 0.6, 0));
  const foot = box(0.8, 0.1, 0.5, m, 0, 0.35, 0.65);
  foot.rotation.x = 0.3;
  g.add(foot);
  return g;
}

export function cooler(): THREE.Group {
  const g = new THREE.Group();
  g.add(box(0.7, 0.42, 0.45, mat('#c8282e', { rough: 0.5 }), 0, 0.21, 0));
  g.add(box(0.72, 0.09, 0.47, mat('#f4f4f4', { rough: 0.4 }), 0, 0.46, 0));
  g.add(box(0.3, 0.04, 0.05, M.black, 0, 0.52, 0));
  return g;
}

export function kettleGrill(): THREE.Group {
  const g = new THREE.Group();
  const black = mat('#1e1e1e', { rough: 0.5, metal: 0.4 });
  const bowl = mesh(
    new THREE.SphereGeometry(0.34, 14, 8, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2),
    black,
    0,
    0.8,
    0,
  );
  g.add(bowl);
  const lid = mesh(new THREE.SphereGeometry(0.34, 14, 8, 0, Math.PI * 2, 0, Math.PI / 2), black, 0, 0.84, 0);
  lid.rotation.z = 0.5;
  lid.position.x = -0.12;
  g.add(lid);
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2;
    const leg = cyl(0.02, 0.02, 0.8, M.steel, Math.cos(a) * 0.22, 0.4, Math.sin(a) * 0.22, 6);
    leg.rotation.z = Math.cos(a) * 0.15;
    leg.rotation.x = -Math.sin(a) * 0.15;
    g.add(leg);
  }
  return g;
}

export function oilDrumSmoker(): THREE.Group {
  const g = new THREE.Group();
  const black = mat('#262626', { rough: 0.6, metal: 0.3 });
  const drum = cyl(0.32, 0.32, 1.0, black, 0, 0.82, 0, 14);
  drum.rotation.z = Math.PI / 2;
  g.add(drum);
  g.add(cyl(0.07, 0.07, 0.7, black, -0.35, 1.3, 0, 8));
  for (const x of [-0.4, 0.4])
    for (const z of [-0.22, 0.22]) g.add(box(0.05, 0.55, 0.05, M.steel, x, 0.27, z));
  g.add(box(0.5, 0.04, 0.05, M.chrome, 0, 1.02, 0.3));
  return g;
}

export function lawnChair(color: string): THREE.Group {
  const g = new THREE.Group();
  const frame = M.chrome;
  const web = mat(color, { rough: 0.8 });
  g.add(box(0.55, 0.04, 0.5, web, 0, 0.42, 0));
  const back = box(0.55, 0.6, 0.04, web, 0, 0.72, -0.27);
  back.rotation.x = -0.2;
  g.add(back);
  for (const x of [-0.27, 0.27]) {
    g.add(cyl(0.015, 0.015, 0.45, frame, x, 0.22, 0.22, 5));
    g.add(cyl(0.015, 0.015, 1.0, frame, x, 0.5, -0.25, 5));
    g.add(box(0.03, 0.03, 0.5, frame, x, 0.6, 0));
  }
  return g;
}

export function rockingChair(): THREE.Group {
  const g = new THREE.Group();
  const w = mat('#7a5530');
  g.add(box(0.6, 0.06, 0.55, w, 0, 0.45, 0));
  g.add(box(0.6, 0.75, 0.05, w, 0, 0.85, -0.27));
  for (const x of [-0.28, 0.28]) {
    const rocker = mesh(new THREE.TorusGeometry(0.9, 0.025, 4, 16, 0.9), w, x, 0.95, 0);
    rocker.rotation.y = Math.PI / 2;
    rocker.rotation.z = -Math.PI / 2 - 0.45;
    g.add(rocker);
    g.add(box(0.04, 0.4, 0.04, w, x, 0.25, 0.22));
    g.add(box(0.04, 0.4, 0.04, w, x, 0.25, -0.22));
  }
  return g;
}

export function mailbox(label: string): THREE.Group {
  const g = new THREE.Group();
  g.add(box(0.1, 1.05, 0.1, M.pole, 0, 0.52, 0));
  const body = cyl(0.14, 0.14, 0.48, mat('#9aa0a6', { metal: 0.5, rough: 0.5 }), 0, 1.12, 0, 12);
  body.rotation.x = Math.PI / 2;
  g.add(body);
  g.add(box(0.28, 0.14, 0.48, mat('#9aa0a6', { metal: 0.5, rough: 0.5 }), 0, 1.06, 0));
  g.add(box(0.02, 0.18, 0.08, mat('#d62828'), 0.15, 1.24, -0.12));
  const tag = signTexture({
    w: 128,
    h: 64,
    bg: '#ffffff',
    lines: [{ text: label, size: 44, font: 'Anton' }],
  });
  const plate = mesh(new THREE.PlaneGeometry(0.3, 0.15), texMat(tag), 0.146, 1.05, 0, { cast: false });
  plate.rotation.y = Math.PI / 2;
  g.add(plate);
  return g;
}

export function trashCan(rng: Random): THREE.Group {
  const g = new THREE.Group();
  const m = mat('#7d8288', { metal: 0.5, rough: 0.5 });
  g.add(cyl(0.3, 0.26, 0.85, m, 0, 0.43, 0, 12));
  const lid = cyl(0.33, 0.33, 0.06, m, 0, 0.9, 0, 12);
  if (rng.chance(0.4)) {
    lid.position.set(0.45, 0.04, 0.1);
    lid.rotation.z = 1.4;
  }
  g.add(lid);
  return g;
}

export function trashBags(rng: Random, n = 3): THREE.Group {
  const g = new THREE.Group();
  const m = mat('#141414', { rough: 0.35 });
  for (let i = 0; i < n; i++) {
    const b = sphere(0.32, m, rng.range(-0.5, 0.5), 0.26, rng.range(-0.4, 0.4), 8);
    b.scale.set(1, rng.range(0.7, 1), 1);
    g.add(b);
  }
  return g;
}

export function dumpster(): THREE.Group {
  const g = new THREE.Group();
  const green = mat('#2f5d3a', { rough: 0.7, metal: 0.2 });
  g.add(box(2.4, 1.25, 1.5, green, 0, 0.72, 0));
  const lid1 = box(1.18, 0.06, 1.55, mat('#1a1a1a'), -0.6, 1.38, 0);
  lid1.rotation.z = 0.05;
  g.add(lid1);
  const lid2 = box(1.18, 0.06, 1.55, mat('#1a1a1a'), 0.75, 1.62, -0.35);
  lid2.rotation.x = -0.7;
  g.add(lid2);
  for (const x of [-1, 1]) for (const z of [-0.6, 0.6]) g.add(cyl(0.08, 0.08, 0.1, M.rubber, x, 0.05, z, 8));
  return g;
}

export interface Pole {
  group: THREE.Group;
  /** Wire attach points in the pole's local frame. */
  anchors: THREE.Vector3[];
}

export function powerPole(withTransformer = false): Pole {
  const g = new THREE.Group();
  g.add(cyl(0.13, 0.17, 9, M.pole, 0, 4.5, 0, 8));
  g.add(box(2.2, 0.14, 0.14, M.pole, 0, 8.4, 0));
  for (const x of [-0.95, 0, 0.95])
    g.add(cyl(0.04, 0.05, 0.16, mat('#5a7f5a', { rough: 0.3 }), x, 8.55, 0, 6));
  if (withTransformer) g.add(cyl(0.3, 0.3, 0.8, mat('#8b9298', { metal: 0.4 }), 0, 7.3, 0.3, 10));
  return { group: g, anchors: [new THREE.Vector3(-0.95, 8.6, 0), new THREE.Vector3(0.95, 8.6, 0)] };
}

/** A sagging wire between two world points. */
export function wire(a: THREE.Vector3, b: THREE.Vector3, sag = 0.8): THREE.Mesh {
  const pts: THREE.Vector3[] = [];
  for (let i = 0; i <= 8; i++) {
    const t = i / 8;
    const p = new THREE.Vector3().lerpVectors(a, b, t);
    p.y -= Math.sin(t * Math.PI) * sag;
    pts.push(p);
  }
  const geo = new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 10, 0.02, 3, false);
  const m = new THREE.Mesh(geo, mat('#151515'));
  m.castShadow = false;
  return m;
}

let streetBulb: THREE.MeshStandardMaterial | null = null;
export function streetLight(): { group: THREE.Group; lamp: THREE.Vector3 } {
  const g = new THREE.Group();
  g.add(cyl(0.08, 0.11, 6.5, M.steel, 0, 3.25, 0, 8));
  g.add(box(0.1, 0.1, 1.8, M.steel, 0, 6.45, 0.85));
  g.add(box(0.36, 0.14, 0.6, mat('#3a3d40'), 0, 6.38, 1.7));
  if (!streetBulb) {
    streetBulb = new THREE.MeshStandardMaterial({ color: '#fff1c8', emissive: new THREE.Color('#ffcf73') });
    registerNightGlow(streetBulb, 2.5);
  }
  g.add(mesh(new THREE.BoxGeometry(0.3, 0.04, 0.5), streetBulb, 0, 6.3, 1.7, { cast: false }));
  return { group: g, lamp: new THREE.Vector3(0, 6.1, 1.7) };
}

export function pineTree(rng: Random): THREE.Group {
  const g = new THREE.Group();
  const s = rng.range(0.8, 1.5);
  g.add(cyl(0.16 * s, 0.22 * s, 1.6 * s, M.trunk, 0, 0.8 * s, 0, 6));
  const greens = ['#2f5d3a', '#36663f', '#284f32', '#3b6b40'];
  const green = mat(rng.pick(greens), { flat: true });
  for (let i = 0; i < 3; i++) {
    const r = (1.7 - i * 0.42) * s;
    g.add(mesh(new THREE.ConeGeometry(r, 2.2 * s, 7), green, 0, (1.9 + i * 1.25) * s, 0));
  }
  g.rotation.y = rng.range(0, Math.PI * 2);
  return g;
}

export function leafyTree(rng: Random): THREE.Group {
  const g = new THREE.Group();
  const s = rng.range(0.9, 1.4);
  g.add(cyl(0.18 * s, 0.26 * s, 2.6 * s, M.trunk, 0, 1.3 * s, 0, 7));
  const leaf = mat(rng.pick(['#4f7a32', '#5c8a3a', '#466e2c']), { flat: true });
  for (let i = 0; i < 4; i++) {
    const blob = mesh(
      new THREE.IcosahedronGeometry(rng.range(1.0, 1.5) * s, 0),
      leaf,
      rng.range(-0.8, 0.8) * s,
      (3 + rng.range(0, 1.4)) * s,
      rng.range(-0.8, 0.8) * s,
    );
    g.add(blob);
  }
  return g;
}

export function bush(rng: Random): THREE.Mesh {
  const b = mesh(
    new THREE.IcosahedronGeometry(rng.range(0.4, 0.8), 0),
    mat(rng.pick(['#4a6b2e', '#557a33', '#3e5c27']), { flat: true }),
    0,
    0.3,
    0,
  );
  b.scale.y = 0.7;
  return b;
}

/** A sign board on posts. The sign face points +z. */
export function signBoard(
  spec: SignSpec,
  width: number,
  height: number,
  postHeight = 1.2,
  posts = 2,
): THREE.Group {
  const g = new THREE.Group();
  const face = mesh(
    new THREE.PlaneGeometry(width, height),
    texMat(signTexture(spec)),
    0,
    postHeight + height / 2,
    0.06,
    { cast: false },
  );
  g.add(face);
  g.add(box(width + 0.1, height + 0.1, 0.1, M.pole, 0, postHeight + height / 2, 0));
  if (posts === 2) {
    for (const x of [-width / 2 + 0.2, width / 2 - 0.2])
      g.add(box(0.14, postHeight + height, 0.14, M.pole, x, (postHeight + height) / 2, -0.1));
  } else if (posts === 1) {
    g.add(box(0.14, postHeight, 0.14, M.pole, 0, postHeight / 2, -0.1));
  }
  return g;
}

/** Flag on a pole; the returned flag mesh has a segmented plane for waving. */
export function flagpole(
  height: number,
  flagMat: THREE.Material,
  w = 1.9,
  h = 1.0,
): { group: THREE.Group; flag: THREE.Mesh } {
  const g = new THREE.Group();
  g.add(cyl(0.05, 0.07, height, M.chrome, 0, height / 2, 0, 8));
  g.add(sphere(0.09, mat('#d4af37', { metal: 0.8, rough: 0.3 }), 0, height + 0.05, 0, 8));
  const geo = new THREE.PlaneGeometry(w, h, 12, 4);
  geo.translate(w / 2, 0, 0);
  const flag = new THREE.Mesh(geo, flagMat);
  flag.position.set(0.05, height - h / 2 - 0.1, 0);
  flag.castShadow = true;
  flag.userData.noBatch = true;
  g.add(flag);
  return { group: g, flag };
}

/** Animate a flag built by `flagpole` (vertex wave). */
export function waveFlag(flag: THREE.Mesh, time: number, strength = 1): void {
  const pos = (flag.geometry as THREE.BufferGeometry).attributes.position as THREE.BufferAttribute;
  if (!flag.userData.base) flag.userData.base = Float32Array.from(pos.array as Float32Array);
  const base = flag.userData.base as Float32Array;
  for (let i = 0; i < pos.count; i++) {
    const x = base[i * 3] as number;
    const y = base[i * 3 + 1] as number;
    pos.setZ(i, Math.sin(x * 2.4 - time * 5 + y * 0.6) * 0.14 * x * strength);
  }
  pos.needsUpdate = true;
  flag.geometry.computeVertexNormals();
}

export function doghouse(): THREE.Group {
  const g = new THREE.Group();
  const wood = texMat(woodTexture(), { color: '#c9a27a' });
  g.add(box(1.2, 0.9, 1.3, wood, 0, 0.45, 0));
  for (const s of [-1, 1]) {
    const r = box(0.95, 0.08, 1.4, mat('#8a2b22'), s * 0.36, 1.08, 0);
    r.rotation.z = -s * 0.6;
    g.add(r);
  }
  g.add(box(0.5, 0.6, 0.02, M.black, 0, 0.35, 0.66, { cast: false }));
  return g;
}

export function tireStack(rng: Random, n = 4): THREE.Group {
  const g = new THREE.Group();
  for (let i = 0; i < n; i++) {
    const t = mesh(
      new THREE.TorusGeometry(0.33, 0.13, 6, 12),
      M.rubber,
      rng.range(-0.05, 0.05),
      0.13 + i * 0.24,
      rng.range(-0.05, 0.05),
    );
    t.rotation.x = Math.PI / 2;
    g.add(t);
  }
  return g;
}

export function washer(color = '#ecebe6'): THREE.Group {
  const g = new THREE.Group();
  g.add(box(0.7, 0.9, 0.7, mat(color), 0, 0.45, 0));
  const door = cyl(0.22, 0.22, 0.03, M.darkGlass, 0, 0.5, 0.36, 12);
  door.rotation.x = Math.PI / 2;
  g.add(door);
  return g;
}

export function fridge(color = '#f0ede4'): THREE.Group {
  const g = new THREE.Group();
  g.add(box(0.75, 1.7, 0.7, mat(color), 0, 0.85, 0));
  g.add(box(0.04, 0.5, 0.05, M.chrome, 0.28, 1.15, 0.37));
  g.add(box(0.76, 0.02, 0.71, mat('#cfcbc2'), 0, 1.2, 0, { cast: false }));
  return g;
}

export function mattress(): THREE.Group {
  const g = new THREE.Group();
  const m = box(1.9, 0.25, 1.4, mat('#e8dcc0'), 0, 0.13, 0);
  g.add(m);
  g.add(
    box(0.6, 0.01, 0.5, mat('#b8a06a', { transparent: true, opacity: 0.7 }), 0.3, 0.26, 0.2, { cast: false }),
  );
  return g;
}

export function basketballHoop(): THREE.Group {
  const g = new THREE.Group();
  g.add(cyl(0.07, 0.07, 3.3, M.steel, 0, 1.65, 0, 8));
  g.add(box(1.2, 0.8, 0.05, mat('#f4f4f4'), 0, 3.4, 0.1));
  g.add(box(0.5, 0.36, 0.01, mat('#d33'), 0, 3.3, 0.13, { cast: false }));
  const rim = mesh(new THREE.TorusGeometry(0.23, 0.02, 4, 14), mat('#e2571e'), 0, 3.1, 0.38);
  rim.rotation.x = Math.PI / 2;
  g.add(rim);
  return g;
}

export function bmxRamp(): THREE.Group {
  const g = new THREE.Group();
  const shape = new THREE.Shape();
  shape.moveTo(-1, 0);
  shape.lineTo(1, 0);
  shape.lineTo(1, 0.7);
  shape.closePath();
  const geo = new THREE.ExtrudeGeometry(shape, { depth: 1.2, bevelEnabled: false });
  geo.translate(0, 0, -0.6);
  g.add(mesh(geo, texMat(woodTexture(), { color: '#d9c29a' })));
  return g;
}

export function bike(color: string): THREE.Group {
  const g = new THREE.Group();
  for (const x of [-0.45, 0.45]) {
    const w = mesh(new THREE.TorusGeometry(0.28, 0.03, 4, 14), M.rubber, x, 0.3, 0);
    g.add(w);
  }
  const frame = mat(color, { metal: 0.4, rough: 0.4 });
  const top = box(0.7, 0.04, 0.04, frame, 0, 0.55, 0);
  top.rotation.z = 0.1;
  g.add(top);
  const down = box(0.6, 0.04, 0.04, frame, -0.12, 0.42, 0);
  down.rotation.z = -0.7;
  g.add(down);
  g.add(box(0.04, 0.35, 0.4, M.black, 0.4, 0.72, 0));
  g.add(box(0.2, 0.05, 0.1, M.black, -0.3, 0.7, 0));
  g.rotation.x = 0.35;
  return g;
}

export function waterTower(label: string): THREE.Group {
  const g = new THREE.Group();
  const legs = M.steel;
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
    const leg = cyl(0.12, 0.12, 14, legs, Math.cos(a) * 2.4, 7, Math.sin(a) * 2.4, 6);
    g.add(leg);
  }
  g.add(cyl(0.2, 0.2, 14, legs, 0, 7, 0, 6));
  const tankMat = texMat(
    // Wraps once around the tank (circumference 22.6 × 4 tall), so keep that shape.
    signTexture({
      w: 724,
      h: 128,
      bg: '#cfd6d9',
      lines: [{ text: label, size: 64, font: 'Anton', color: '#1d3b6e' }],
    }),
    { rough: 0.5, metal: 0.3 },
  );
  g.add(cyl(3.6, 3.6, 4, tankMat, 0, 16, 0, 20));
  g.add(mesh(new THREE.ConeGeometry(3.8, 1.6, 20), mat('#b7c0c4', { metal: 0.3 }), 0, 18.8, 0));
  g.add(
    mesh(
      new THREE.SphereGeometry(3.6, 20, 8, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2),
      mat('#b7c0c4'),
      0,
      14,
      0,
    ),
  );
  return g;
}

export function picketFence(length: number, color = '#f4f2ea'): THREE.Group {
  const g = new THREE.Group();
  const m = mat(color);
  for (let x = -length / 2; x <= length / 2; x += 0.22) g.add(box(0.09, 0.8, 0.03, m, x, 0.4, 0));
  g.add(box(length, 0.06, 0.03, m, 0, 0.6, -0.03));
  g.add(box(length, 0.06, 0.03, m, 0, 0.25, -0.03));
  return g;
}

let chainLinkMat: THREE.MeshStandardMaterial | null = null;
/** Chain-link fence along +x of the given length. */
export function chainLinkFence(length: number, height = 2.2): THREE.Group {
  const g = new THREE.Group();
  if (!chainLinkMat) {
    const tex = chainLinkTexture();
    chainLinkMat = new THREE.MeshStandardMaterial({
      map: tex,
      transparent: false,
      alphaTest: 0.4,
      side: THREE.DoubleSide,
      metalness: 0.5,
      roughness: 0.5,
    });
  }
  const geo = new THREE.PlaneGeometry(length, height);
  const uv = geo.attributes.uv as THREE.BufferAttribute;
  for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * length * 2.2, uv.getY(i) * height * 2.2);
  const fence = mesh(geo, chainLinkMat, 0, height / 2, 0, { cast: false });
  g.add(fence);
  for (let x = -length / 2; x <= length / 2 + 0.01; x += Math.max(2.5, length / Math.ceil(length / 3)))
    g.add(cyl(0.04, 0.04, height + 0.1, M.steel, x, (height + 0.1) / 2, 0, 6));
  g.add(box(length, 0.05, 0.05, M.steel, 0, height, 0, { cast: false }));
  return g;
}

export function firePit(): THREE.Group {
  const g = new THREE.Group();
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2;
    const stone = box(0.3, 0.22, 0.2, M.cinder, Math.cos(a) * 0.6, 0.11, Math.sin(a) * 0.6);
    stone.rotation.y = -a;
    g.add(stone);
  }
  g.add(cyl(0.5, 0.5, 0.04, mat('#2b2420'), 0, 0.03, 0, 10, { cast: false }));
  return g;
}

export function crushedCar(color: string, rng: Random): THREE.Group {
  const g = new THREE.Group();
  const m = mat(color, { rough: 0.95 });
  const b = box(3.8, 0.9, 1.7, m, 0, 0.45, 0);
  g.add(b);
  g.add(box(1.6, 0.4, 1.4, M.darkGlass, rng.range(-0.4, 0.4), 1.05, 0));
  g.rotation.y = rng.range(-0.2, 0.2);
  return g;
}

export function pennants(a: THREE.Vector3, b: THREE.Vector3, colors: string[]): THREE.Group {
  const g = new THREE.Group();
  g.add(wire(a, b, 0.5));
  const n = Math.floor(a.distanceTo(b) / 0.6);
  for (let i = 1; i < n; i++) {
    const t = i / n;
    const p = new THREE.Vector3().lerpVectors(a, b, t);
    p.y -= Math.sin(t * Math.PI) * 0.5;
    const tri = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(-0.16, 0, 0),
      new THREE.Vector3(0.16, 0, 0),
      new THREE.Vector3(0, -0.36, 0),
    ]);
    tri.computeVertexNormals();
    const flag = new THREE.Mesh(tri, mat(colors[i % colors.length] as string, { side: THREE.DoubleSide }));
    flag.position.copy(p);
    flag.lookAt(p.x + (b.z - a.z), p.y, p.z - (b.x - a.x));
    g.add(flag);
  }
  return g;
}

export function bench(): THREE.Group {
  const g = new THREE.Group();
  const wood = texMat(woodTexture());
  g.add(box(1.6, 0.06, 0.4, wood, 0, 0.45, 0));
  g.add(box(1.6, 0.35, 0.05, wood, 0, 0.7, -0.2));
  for (const x of [-0.7, 0.7]) g.add(box(0.06, 0.45, 0.4, M.black, x, 0.22, 0));
  return g;
}

export function gasPump(): THREE.Group {
  const g = new THREE.Group();
  g.add(box(0.9, 0.2, 0.7, M.cinder, 0, 0.1, 0));
  g.add(box(0.7, 1.7, 0.45, mat('#e8e8e8'), 0, 1.05, 0));
  g.add(box(0.72, 0.42, 0.47, mat('#2a7bd1'), 0, 1.71, 0));
  g.add(box(0.4, 0.25, 0.02, M.darkGlass, 0, 1.3, 0.24, { cast: false }));
  g.add(box(0.1, 0.25, 0.1, M.black, 0.4, 1.1, 0));
  return g;
}

export function payphone(): THREE.Group {
  const g = new THREE.Group();
  g.add(box(0.12, 1.2, 0.12, M.steel, 0, 0.6, 0));
  g.add(box(0.5, 0.7, 0.3, mat('#9da3a8', { metal: 0.5 }), 0, 1.45, 0.05));
  g.add(box(0.12, 0.3, 0.08, M.black, -0.15, 1.5, 0.22));
  g.add(box(0.5, 0.12, 0.31, mat('#1d4e9e'), 0, 1.86, 0.05));
  return g;
}

export function iceChest(): THREE.Group {
  const g = new THREE.Group();
  const label = signTexture({
    w: 256,
    h: 128,
    bg: '#e9f6ff',
    lines: [{ text: 'ICE', size: 90, font: 'Anton', color: '#1f6fd1' }],
  });
  g.add(box(1.4, 1.2, 0.8, mat('#e9f6ff'), 0, 0.6, 0));
  g.add(mesh(new THREE.PlaneGeometry(1.0, 0.5), texMat(label), 0, 0.7, 0.41, { cast: false }));
  return g;
}

export function cinderBlock(): THREE.Mesh {
  return box(0.4, 0.2, 0.2, M.cinder, 0, 0.1, 0);
}

export function deckPlatform(w: number, d: number, h: number): THREE.Group {
  const g = new THREE.Group();
  g.add(mesh(worldBox(w, 0.1, d, 1), texMat(woodTexture()), 0, h - 0.05, 0));
  for (const x of [-w / 2 + 0.1, w / 2 - 0.1])
    for (const z of [-d / 2 + 0.1, d / 2 - 0.1]) g.add(box(0.1, h, 0.1, M.trunk, x, h / 2, z));
  return g;
}
